
// Game initialization logic
import { BlockStackGameScene, initBlockStackGameScene } from './blockStackGameScene';
import { GAME_CONFIG } from './gameConfig';

// Initialize the Phaser game instance
export function initGame(containerId) {
  return new Promise((resolve, reject) => {
    try {
      console.log('Initializing BlockStack game...');
      
      // If a previous game exists, destroy it
      if (window.blockStackGame) {
        try {
          console.log('Destroying previous BlockStack game instance...');
          window.blockStackGame.destroy(true, true);
          window.blockStackGame = null;
          console.log('Previous BlockStack game instance destroyed');
        } catch (e) {
          console.warn('Error destroying previous game instance:', e);
        }
      }
      
      // Check if container exists
      const container = document.getElementById(containerId);
      if (!container) {
        console.error(`Container with ID ${containerId} not found`);
        return reject(new Error(`Game container with ID ${containerId} not found`));
      }
      
      // Get current browser and device information for diagnostics
      const userAgent = navigator.userAgent;
      console.log('Browser info for BlockStack game:', userAgent);
      
      // Check if we're on mobile or an uncommon browser like Opera
      const isNonStandardBrowser = /Opera|OPR|Opera Mini|UCBrowser|Edge/i.test(userAgent);
      const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(userAgent);
      
      // More reliable Phaser detection
      if (typeof window.Phaser === 'undefined') {
        console.error('Phaser is not defined, attempting to load it dynamically');
        
        // Create a promise to load Phaser
        const loadPhaserDynamically = () => {
          return new Promise((resolvePhaser, rejectPhaser) => {
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/phaser@3.55.2/dist/phaser.min.js';
            script.async = true;
            script.onload = () => {
              console.log('Phaser loaded dynamically:', window.Phaser?.VERSION);
              resolvePhaser();
            };
            script.onerror = () => {
              console.error('Failed to load Phaser dynamically');
              rejectPhaser(new Error('Failed to load game engine. Please refresh the page.'));
            };
            document.head.appendChild(script);
          });
        };
        
        // Load Phaser and then initialize the game
        loadPhaserDynamically()
          .then(() => {
            // Double check Phaser is available
            if (typeof window.Phaser === 'undefined') {
              return reject(new Error('Game engine could not be loaded. Please try using Chrome or Firefox.'));
            }
            
            // Continue with game initialization
            initializeGameWithPhaser();
          })
          .catch(reject);
          
        return;
      } else {
        // Phaser is already loaded, proceed with initialization
        initializeGameWithPhaser();
      }
      
      // Helper function to create game once Phaser is loaded
      function initializeGameWithPhaser() {
        // Configure the Phaser game with enhanced compatibility options
        const config = {
          type: isNonStandardBrowser ? window.Phaser.CANVAS : window.Phaser.AUTO, // Force CANVAS for Opera
          parent: containerId,
          width: GAME_CONFIG.gameWidth,
          height: GAME_CONFIG.gameHeight,
          backgroundColor: GAME_CONFIG.backgroundColor,
          scene: [initBlockStackGameScene()],
          scale: {
            mode: window.Phaser.Scale.FIT,
            autoCenter: window.Phaser.Scale.CENTER_BOTH
          },
          disableContextMenu: true, // Prevent context menu on right-click
          render: {
            pixelArt: false,
            antialias: true,
            roundPixels: true
          },
          // Always include input options for better mobile support
          input: {
            activePointers: 3,
            touch: { capture: true }
          },
          banner: false // Disable Phaser banner for production
        };
        
        // Apply additional settings for Opera and other browsers that need special handling
        if (isNonStandardBrowser) {
          console.log('Detected Opera or other non-standard browser, applying compatibility settings');
          config.render.transparent = false;
          // For browsers with performance issues, reduce visual quality
          config.render.pixelArt = true;
        }

        // Create the game instance with error handling
        console.log('Creating Phaser game with config:', config);
        
        try {
          // Add some extra robustness by wrapping the game creation
          setTimeout(() => {
            try {
              window.blockStackGame = new window.Phaser.Game(config);
              
              // Check if game was created successfully
              if (!window.blockStackGame) {
                return reject(new Error('Failed to create game instance'));
              }
              
              // Listen for the game ready event
              if (window.blockStackGame.events) {
                window.blockStackGame.events.once('ready', () => {
                  console.log('BlockStack game ready event fired');
                  resolve(window.blockStackGame);
                });
              }
              
              // Give the game a moment to initialize before resolving
              // This is a fallback in case the ready event doesn't fire
              setTimeout(() => {
                console.log('BlockStack game initialized successfully');
                resolve(window.blockStackGame);
              }, 1500); 
            } catch (innerError) {
              console.error('Error in game creation:', innerError);
              reject(new Error('Failed to initialize the game. Please try again.'));
            }
          }, 100);
        } catch (gameError) {
          console.error('Error creating Phaser game:', gameError);
          reject(new Error('Failed to create the game. Please refresh the page.'));
        }
      }
    } catch (error) {
      console.error('Failed to initialize BlockStack game:', error);
      reject(new Error('Game initialization failed. Please refresh the page.'));
    }
  });
}
